import { ShieldCheck } from "lucide-react";
import { ScrollReveal, Overline } from "./Reveal";

const SPECS = [
  ["Adhesion", "12-hour sweat-proof hold"],
  ["Airflow", "+38% nasal opening"],
  ["Fit", "Universal flex band"],
  ["Skin", "Latex-free, hypoallergenic"],
  ["Pack", "30 strips — matte black"],
];

const CERTS = ["TGA Certified", "REACH Certified", "PFAS-Free Materials"];

const ProductSpotlight = () => (
  <section
    id="product"
    data-testid="product-section"
    className="relative py-28 md:py-40 border-t border-white/10"
  >
    <div className="px-6 md:px-10 max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
      <ScrollReveal className="lg:col-span-5 order-2 lg:order-1">
        <Overline className="mb-4">Flagship — N°001</Overline>
        <h2 className="font-anton uppercase text-4xl md:text-6xl leading-[0.9]">
          The Strip<span className="text-volt">.</span>
        </h2>
        <p className="mt-6 text-white/60 font-light leading-relaxed text-base md:text-lg max-w-md">
          One strip. Zero gimmicks. Opens your nose by 38% and holds for 12
          hours — through training, sweat and sleep.
        </p>

        <ul data-testid="product-spec-list" className="mt-10 border-t border-white/10">
          {SPECS.map(([label, value]) => (
            <li
              key={label}
              className="flex items-baseline justify-between gap-6 border-b border-white/10 py-4 group"
            >
              <span className="font-plex text-xs uppercase tracking-[0.25em] text-white/40 group-hover:text-volt transition-colors duration-300">
                {label}
              </span>
              <span className="text-sm md:text-base text-white/85 text-right">{value}</span>
            </li>
          ))}
        </ul>

        <div data-testid="product-certifications" className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3">
          {CERTS.map((cert) => (
            <span
              key={cert}
              className="flex items-center gap-2 font-plex text-[11px] uppercase tracking-[0.22em] text-white/60"
            >
              <ShieldCheck size={15} className="text-volt" strokeWidth={2.2} />
              {cert}
            </span>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-6">
          <a
            href="#pricing"
            data-testid="product-buy-button"
            className="bg-volt text-black font-anton uppercase text-base md:text-lg px-8 py-4 tracking-wider transition-[transform,background-color] duration-200 hover:bg-volt-dark active:scale-95 clip-tag"
          >
            Buy — from $14.95
          </a>
          <span className="font-plex text-xs uppercase tracking-[0.25em] text-white/40">
            First pack $14.95 — usually $27.95
          </span>
        </div>
      </ScrollReveal>

      <ScrollReveal delay={0.15} className="lg:col-span-7 order-1 lg:order-2">
        <div
          data-testid="product-visual-frame"
          className="relative clip-frame border border-white/10 bg-[#121212] stage-light overflow-hidden aspect-square"
        >
          <div className="absolute inset-0 grid-lines opacity-60" />
          <img
            src="/strip-layers.png"
            alt="Breethe N°001 exploded view — fabric layer, flex bands and adhesive layer"
            data-testid="product-strip-image"
            className="absolute inset-0 h-full w-full object-contain p-8 md:p-12 drop-shadow-[0_40px_60px_rgba(0,0,0,0.85)]"
          />
          <div className="absolute bottom-4 left-6 right-6 z-10 flex items-center justify-between font-plex text-[10px] uppercase tracking-[0.25em] text-white/70">
            <span className="annot-chip bg-black/70 px-3 py-2">Exploded view — 3 layers</span>
            <span className="annot-chip bg-black/70 px-3 py-2">N°001</span>
          </div>

          <div className="hidden lg:block">
            <div className="absolute top-10 left-10 z-10 annot-chip bg-black/70 px-3 py-2 font-plex text-[10px] uppercase tracking-[0.25em] text-white/70">
              <span className="text-volt">01 /</span> Micro-vent weave
              <span className="block mt-2 w-24 h-px bg-white/25" />
            </div>
            <div className="absolute top-1/2 right-8 z-10 annot-chip bg-black/70 px-3 py-2 font-plex text-[10px] uppercase tracking-[0.25em] text-white/70 text-right">
              <span className="text-volt">02 /</span> Reinforced flex band
              <span className="block mt-2 ml-auto w-24 h-px bg-white/25" />
            </div>
            <div className="absolute bottom-28 left-10 z-10 annot-chip bg-black/70 px-3 py-2 font-plex text-[10px] uppercase tracking-[0.25em] text-white/70">
              <span className="text-volt">03 /</span> 12H sweat-proof adhesive
              <span className="block mt-2 w-24 h-px bg-white/25" />
            </div>
          </div>

          <span className="absolute top-4 right-6 font-plex text-[10px] uppercase tracking-[0.3em] text-white/30">
            Breethe N°001 — Scale 1:1
          </span>
        </div>
      </ScrollReveal>
    </div>
  </section>
);

export default ProductSpotlight;
